import { MailIcon, PhoneIcon } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { BsTwitterX } from 'react-icons/bs';
import { FaInstagram, FaStreetView, FaTelegram, FaYoutube } from 'react-icons/fa';
import { FaLocationPin, FaTiktok, FaWhatsapp } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

export default function Contact() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const clickMaps = () => {
    // Perform any logic here before navigating
    window.open("https://maps.app.goo.gl/orU8WGav3zU4Ma2A6", "_blank")
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.75 && rect.bottom > 0;
        
        if (isVisible) {
          setVisibleSections(prev => new Set([...prev, section.getAttribute('data-section')]));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const executeCaptcha = () => {
    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute("6LcIY4EsAAAAADoIsHMQruBeXbRjcC2MdVvnJ0j7", { action: "contact" })
          .then((token) => {
            resolve(token);
          });
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = await executeCaptcha();

    const formDatas = new FormData();
    Object.keys(formDatas).forEach((key) => {
      formDatas.append(key, formDatas[key]);
    });

    formDatas.append("captcha_token", token);

    // Member
    formDatas.append("name", formData.name);
    formDatas.append("email", formData.email);
    formDatas.append("subject",formData.subject);
    formDatas.append("message", formData.message);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/contact/send`,
        {
          method: "POST",
          body: formDatas,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        alert("Error: " + result.message);
        return;
      }
      Swal.fire({
        title: "Sukses mengirim Pesan",
        text: "Terima kasih telah mengirim pesan kepada Kami, tim Customer Service Kami akan segera merespon pesan Anda",
        icon: "success",
        draggable: true
      }).then(() => {
        setSubmitted(true);
      });
    } catch (err) {
      console.error(err);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-blue-950/30 pt-24 flex items-center justify-center px-6">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-hover p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-amber-pale flex items-center justify-center mx-auto mb-6 text-4xl">
            🎉
          </div>
          <h2 className="font-display font-bold text-navy text-3xl mb-3">Terima kasih Pengunjung kenziekomunika.com</h2>
          <p className="font-body text-slate-muted text-base mb-2">
            Terima kasih, <strong className="text-navy">{formData.name}</strong>. Pesan Anda telah terkirim.
          </p>
          <p className="font-body text-slate-muted text-sm mb-8">
            Tim kami akan segera melakukan pengecekan dan meresponse pesan Anda ke <strong>{formData.email}</strong>.
          </p>
          <a href="/" className="btn-primary justify-center w-full">Kembali ke Beranda</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header Section */}
      <section 
        data-section="header"
        className="py-24 pt-40 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="/images/bg-hero-contact.jpeg"
            alt="background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
          visibleSections.has('header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h1 className="font-bricolage text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-red-600  to-[#3E3274] bg-clip-text text-transparent">
            Mari Terhubung Bersama Kami
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-red-600  to-[#3E3274] mx-auto mb-6"></div>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Butuh bantuan atau ingin bergabung menjadi mitra? Tim Kenzie Komunika siap membantu Anda dengan pelayanan yang cepat dan responsif. Jangan ragu untuk menghubungi kami melalui kontak yang tersedia.<br/>
            <span className='font-bold'>The Best Choice for Online & Payment Products</span>.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section 
        data-section="contact"
        className="py-24 bg-white"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 delay-200 ${
          visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-bricolage text-3xl font-bold mb-6 bg-gradient-to-r from-red-600  to-[#3E3274] bg-clip-text text-transparent">
                  Informasi Kontak
                </h2>
                <p className="text-slate-700 mb-8">
                  Isi Form disamping ini, dan tim cepat Kita akan langsung memberikan respon.
                </p>
              </div>

              <div className="space-y-6">
                <div
                  
                  className="flex items-start space-x-4 p-6 bg-slate-100/70 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-[#3E3274] transition-all duration-300 transform hover:scale-105"
                  style={{ transitionDelay: `${1 * 100}ms` }}
                >
                  <div className="text-3xl text-[#3E3274]"><MailIcon size={24}></MailIcon></div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#3E3274] mb-1">Email</h3>
                    <p className="text-gray-400 text-sm">admin@kenziekomunika.com</p>
                  </div>
                </div>
                <div
                  
                  className="flex items-start space-x-4 p-6 bg-slate-100/70 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-[#3E3274] transition-all duration-300 transform hover:scale-105"
                  style={{ transitionDelay: `${1 * 100}ms` }}
                >
                  <div className="text-3xl text-[#3E3274]"><PhoneIcon size={24}></PhoneIcon></div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#3E3274] mb-1">Telpon</h3>
                    <p className="text-gray-400 text-sm">+622263175009</p>
                  </div>
                </div>
                <div
                  
                  className="flex items-start space-x-4 p-6 bg-slate-100/70 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105"
                  style={{ transitionDelay: `${1 * 100}ms` }}
                >
                  <div className="text-3xl text-[#3E3274]"><FaWhatsapp size={24}></FaWhatsapp></div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#3E3274] mb-1">Whatsapp</h3>
                    <p className="text-gray-400 text-sm">+6285795705113</p>
                    <p className="text-gray-400 text-sm">+6282269500400</p>
                    <p className="text-gray-400 text-sm">+6281221433521</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-xl font-semibold mb-4 text-[#3E3274]">Follow Kami:</h3>
                <div className="flex space-x-2">
                  <a
                    href="https://www.instagram.com/official_kenziekomunika/" target="_blank"
                    className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-red-600 hover:to-[#3E3274]  transition-all duration-300 transform hover:scale-110"
                  >
                    <span className="text-sm text-white font-semibold"><FaInstagram size={24}></FaInstagram></span>
                  </a>
                  <a
                    href="https://t.me/KenzieKomunika" target="_blank"
                    className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-red-600 hover:to-[#3E3274] transition-all duration-300 transform hover:scale-110"
                  >
                    <span className="text-sm text-white font-semibold"><FaTelegram size={24}></FaTelegram></span>
                  </a>
                  <a
                    href="#" target="_blank"
                    className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-red-600 hover:to-[#3E3274] transition-all duration-300 transform hover:scale-110"
                  >
                    <span className="text-sm text-white font-semibold"><FaYoutube size={24}></FaYoutube></span>
                  </a>
                  <a
                    href="https://www.tiktok.com/@kenziekomunika.official" target="_blank"
                    className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-red-600 hover:to-[#3E3274] transition-all duration-300 transform hover:scale-110"
                  >
                    <span className="text-sm text-white font-semibold"><FaTiktok size={24}></FaTiktok></span>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-slate-100/70 backdrop-blur-sm rounded-2xl border border-slate-700 p-8 md:p-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-[#3E3274]">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-slate-700 rounded-full focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all duration-300 text-slate-700 placeholder-gray-500"
                        placeholder="Nama Anda..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2  text-[#3E3274]">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-slate-700 rounded-full focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all duration-300 text-slate-700 placeholder-gray-500"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#3E3274]">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-700 rounded-full focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all duration-300 text-slate-700 placeholder-gray-500"
                      placeholder="Judul Pesan..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#3E3274]">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-white border border-slate-700 rounded-3xl focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all duration-300 text-slate-700 placeholder-gray-500 resize-none"
                      placeholder="Tuliskan pesan Anda..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-red-600  to-[#3E3274] rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-red-500/50 transform hover:scale-105 transition-all duration-300"
                  >
                    Kirim Pesan
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section 
        data-section="map"
        className="py-24 bg-gradient-to-b from-slate-100/95 to-slate-100/40"
      >
        <a
          href="https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=-6.9435976895685805, 107.62701756755487"
          target="_blank"
          className=""
        >
          <div className='flex justify-center p-6'>
            <div className="px-6 flex items-center text-white gap-2 flex-row py-4 bg-gradient-to-r from-red-600  to-[#3E3274] rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 cursor-pointer">
                <FaStreetView size={21}></FaStreetView> Buka Google Street View
            </div>
          </div>
        </a>
        <div className={`cursor-pointer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 delay-300 hover:scale-125 ${
          visibleSections.has('map') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="relative h-105 rounded-2xl overflow-hidden border border-slate-700">
            
            {/* Replace this with actual Google Maps embed */}
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.5533820240594!2d107.627023!3d-6.943855799999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e68e91b24518f0b%3A0xb121d32bb9fbc2e9!2sKenzie%20Komunika!5e0!3m2!1sen!2sid!4v1774429325806!5m2!1sen!2sid" width="100%" height="600" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {/* <section 
        data-section="faq"
        className="py-24 bg-slate-900"
      >
        <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 delay-400 ${
          visibleSections.has('faq') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-400 to-blue-400 bg-clip-text text-transparent">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-blue-500 mx-auto"></div>
          </div>

          <div className="space-y-4">
            {[
              { q: 'What is your typical project timeline?', a: 'Project timelines vary based on scope and complexity. Typically, small projects take 2-4 weeks, medium projects 1-3 months, and large projects 3-6 months or more.' },
              { q: 'Do you offer ongoing support and maintenance?', a: 'Yes! We provide comprehensive support and maintenance packages to ensure your application runs smoothly after launch.' },
              { q: 'What is your development process?', a: 'We follow an agile methodology with regular communication, iterative development, and continuous feedback to ensure we meet your expectations.' },
              { q: 'How do you handle project pricing?', a: 'We offer flexible pricing models including fixed-price projects, time & materials, and dedicated team arrangements based on your needs.' },
            ].map((faq, index) => (
              <div
                key={index}
                className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-blue-500/50 transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-lg font-semibold text-white mb-3 flex items-start">
                  <span className="text-blue-400 mr-3">Q:</span>
                  {faq.q}
                </h3>
                <p className="text-gray-400 ml-8">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <section 
        data-section="cta"
        className="py-24 bg-white relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className={`relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 delay-400 ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="font-bricolage text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-red-600  to-[#3E3274] bg-clip-text text-transparent">
            Ayo bergabung menjadi mitra Kami!
          </h2>
          <p className="text-xl text-slate-700 mb-8">
            Kami tidak hanya menyediakan layanan, tetapi membangun kemitraan jangka panjang untuk perkembangan bisnis Anda.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-red-600  to-[#3E3274] rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300"
          >
            Mulai mendaftar
          </Link>
        </div>
      </section>
    </div>
  );
}
